import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { NgbDropdown, NgbDropdownMenu, NgbDropdownToggle, NgbModal, NgbPagination, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { firstValueFrom, Subscription } from 'rxjs';
import { BrandModel, ClientFullModel, ComponentType, CountryModel, HxBrandService, HxClientService, HxCountryService, HxStoreService, HxUiLabelPipe, StoreBasicModel, toRouterQueryParams } from 'hx-services';
import { HxClientProfileModal } from './client-profile.modal';
import { FormsModule } from '@angular/forms';
import { DatePipe, NgFor, NgIf } from '@angular/common';
import { TranslocoPipe } from '@jsverse/transloco';
@Component({
    selector: 'hx-client-directory.m-grid__item.m-grid__item--fluid.m-wrapper',
    templateUrl: './client-directory.component.html',
    styleUrls: ['./client-directory.component.css'],
    encapsulation: ViewEncapsulation.None,
    imports: [FormsModule, NgIf, NgFor, NgbTooltip, NgbPagination, NgbDropdown, NgbDropdownToggle, NgbDropdownMenu, DatePipe, TranslocoPipe, HxUiLabelPipe]
})
export class HxClientDirectoryComponent implements OnInit, OnDestroy {
  list: ClientFullModel[] = [];
  pagination = {allItemCount: 0};
  loadFailed = false;
  errorId?: string;
  searchDraft = '';
  isLoading = {
    list: false
  };
  brands: BrandModel[] = [];
  countries: CountryModel[] = [];
  availableCountries: CountryModel[] = [];
  private scopeStores: StoreBasicModel[] = [];
  params: {
    page: number;
    limit: number;
    query?: string;
    birthDay?: number;
    birthMonth?: number;
    brandId: number;
    countryId: number;
  } = {page: 1, limit: 20, brandId: 1, countryId: 1};
  days: number[] = Array.from({length: 31}, (_, i) => i + 1);
  months: number[] = Array.from({length: 12}, (_, i) => i + 1);
  pageSizes = [20, 50, 100];
  private sub!: Subscription;
  private loadRequestId = 0;
  private lastQueryParams = '';

  constructor(
    private modal: NgbModal,
    private clientService: HxClientService,
    private aRoute: ActivatedRoute,
    private router: Router,
    private brandService: HxBrandService,
    private countryService: HxCountryService,
    private storeService: HxStoreService,
  ) {}

  ngOnInit() {
    void this.loadScopes();
    this.sub = this.aRoute.queryParamMap.subscribe(paramMap => {
      this.params.page = this.numberParam(paramMap, 'page', 1, 1000000) ?? 1;
      this.params.limit = this.numberParam(paramMap, 'limit', 20, 1000) ?? 20;
      this.params.query = paramMap.get('query') ?? '';
      this.searchDraft = this.params.query;
      this.params.birthDay = this.numberParam(paramMap, 'birthDay', 1, 31);
      this.params.birthMonth = this.numberParam(paramMap, 'birthMonth', 1, 12);
      this.params.brandId = this.numberParam(paramMap, 'brandId', 1, 1000000) ?? 1;
      this.params.countryId = this.numberParam(paramMap, 'countryId', 1, 1000000) ?? 1;
      this.lastQueryParams = JSON.stringify(toRouterQueryParams(this.params));
      this.loadList();
    });
  }

  private async loadScopes() {
    const [brands, countries, stores] = await Promise.allSettled([
      this.brandService.getMyBrands(),
      firstValueFrom(this.countryService.getMyCountries()),
      this.storeService.getAllStoresByCurrentUserAndComponentType(ComponentType.cc)
    ]);
    if (brands.status === 'fulfilled') {this.brands = brands.value;}
    if (countries.status === 'fulfilled') {this.countries = countries.value;}
    if (stores.status === 'fulfilled') {
      this.scopeStores = stores.value;
      this.brands = this.brands.filter(brand => this.scopeStores.some(store => store.brandId === brand.id));
    }
    this.refreshCountryOptions();
  }

  updateItem(client: ClientFullModel) {
    const modalInstance = this.modal.open(HxClientProfileModal, {size: 'lg', windowClass: 'client-profile-window'});
    modalInstance.componentInstance.id = client.client.id;
    modalInstance.componentInstance.brandId = this.params.brandId;
    modalInstance.componentInstance.countryId = this.params.countryId;
    modalInstance.result.then(() => this.loadList(), () => {});
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
    this.loadRequestId++;
  }

  resetFilter(): void {
    this.params = {page: 1, limit: 20, brandId: 1, countryId: 1};
    this.searchDraft = '';
    this.pageChanged(1);
  }

  onSubmitFilter(): void {
    this.params.query = this.searchDraft.trim() || undefined;
    this.pageChanged(1);
  }

  onLimitChanged(): void {
    this.pageChanged(1);
  }

  selectBrand(id: number) {
    this.params.brandId = id;
    this.refreshCountryOptions();
    if (this.availableCountries.length && !this.availableCountries.some(country => country.id === this.params.countryId)) {
      this.params.countryId = this.availableCountries[0].id;
    }
    this.pageChanged(1);
  }

  selectCountry(id: number) {
    if (!this.availableCountries.some(country => country.id === id)) {return;}
    this.params.countryId = id;
    this.pageChanged(1);
  }

  private refreshCountryOptions(): void {
    this.availableCountries = this.scopeStores.length
      ? this.countries.filter(country => this.scopeStores.some(store => store.brandId === this.params.brandId && store.countryId === country.id))
      : this.countries;
  }

  selectDay(day?: number) {
    this.params.birthDay = day;
    this.pageChanged(1);
  }

  selectMonth(month?: number) {
    this.params.birthMonth = month;
    this.pageChanged(1);
  }

  get selectedBrand(): BrandModel | undefined {
    return this.brands.find(brand => brand.id === this.params.brandId);
  }

  get selectedCountry(): CountryModel | undefined {
    return this.countries.find(country => country.id === this.params.countryId);
  }

  get hasActiveFilters(): boolean {
    return !!(this.params.query || this.params.birthDay || this.params.birthMonth ||
      this.params.brandId !== 1 || this.params.countryId !== 1);
  }

  initials(name?: string): string {
    return (name || '').trim().split(/\s+/).slice(0, 2).map(part => part[0]).join('').toUpperCase() || '—';
  }

  async loadList() {
    const requestId = ++this.loadRequestId;
    this.isLoading.list = true;
    this.loadFailed = false;
    this.errorId = undefined;
    this.list = [];
    this.pagination.allItemCount = 0;
    try {
      const result = await this.clientService.getClientList({...this.params});
      if (requestId !== this.loadRequestId) {
        return;
      }
      this.list = result.list || [];
      this.pagination.allItemCount = result.count;
    } catch (error: unknown) {
      if (requestId !== this.loadRequestId) {
        return;
      }
      this.loadFailed = true;
      if (error instanceof HttpErrorResponse && typeof error.error?.id === 'string') {
        this.errorId = error.error.id;
      }
    } finally {
      if (requestId === this.loadRequestId) {
        this.isLoading.list = false;
      }
    }
  }

  pageChanged(page: number) {
    this.params.page = page;
    this.navigateUrl();
  }

  applyFilters() {
    if (this.params.birthDay == null) {
      this.params.birthDay = undefined;
    }
    if (this.params.birthMonth == null) {
      this.params.birthMonth = undefined;
    }
    this.pageChanged(1);
  }

  private navigateUrl() {
    const queryParams = toRouterQueryParams(this.params);
    if (JSON.stringify(queryParams) === this.lastQueryParams) {
      this.loadList();
      return;
    }
    this.router.navigate(['./'], {
      queryParams,
      relativeTo: this.aRoute
    }).then(navigated => {
      // The router does not emit queryParamMap again for an unchanged filter.
      if (!navigated) {
        this.loadList();
      }
    });
  }

  private numberParam(params: ParamMap, name: string, min: number, max: number): number | undefined {
    const value = params.get(name);
    if (!value || !/^\d+$/.test(value)) {
      return undefined;
    }
    const number = Number(value);
    return Number.isSafeInteger(number) && number >= min && number <= max ? number : undefined;
  }
}
